import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DateRangePicker, DetailRowsSkeleton, StatStrip } from '@/components/core';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useDayTarget } from '@/modules/time-attendance/time-tracker/hooks/use-under-time';
import { DEFAULT_TIMESHEETS_ORDER } from '../../graphql/queries';
import { TimesheetBreakdownTable } from '../../components/timesheet-breakdown-table/timesheet-breakdown-table';
import { formatHours } from '../../components/timesheet-rollup-table/timesheet-rollup-table-columns';
import { ScopedDay, TimesheetWeeks } from '../../components/timesheet-weeks/timesheet-weeks';
import {
  useGetTimeSheetEntries,
  useGetUsersTimesheetRollup,
} from '../../hooks/use-timesheet-manager';
import { ALL_VALUE, useTimesheetFilters } from '../../hooks/use-timesheet-filters';
import {
  ResourceBreakdown,
  TimeSheetsPagingResult,
  TimeSheetTask,
} from '../../types/timesheet-manager.types';
import { taskDateRangeClause } from '../../../utils/task-date';

/**
 * An employee's timesheet, opened from a Team Timesheets row with its range and customer / project
 * filters (HR Module Prototype, Component Map › Employee timesheet page): a stat strip, the customer /
 * project breakdown with a Grand total, then the daily entries grouped by week. Hours count only the
 * tasks in scope, but a day's Under time is judged on all of its tasks.
 */
export const UserTimesheetDetailPage = () => {
  const { t } = useTranslation();
  const { userId = '' } = useParams<{ userId: string }>();
  const { filters, setFilters } = useTimesheetFilters();
  const { range } = filters;
  const customerId = filters.customerId === ALL_VALUE ? undefined : filters.customerId;
  const projectId = filters.projectId === ALL_VALUE ? undefined : filters.projectId;
  const dayTarget = useDayTarget(userId);

  const { data: rollupData, isLoading: isRollupLoading } = useGetUsersTimesheetRollup({
    startDate: range.from,
    endDate: range.to,
    customerId,
    projectId,
    userId,
  });
  const resource = ((rollupData as ResourceBreakdown[]) ?? [])[0];

  // Every entry of the range (one a day), to group by week and count the days.
  const { data: entriesData, isLoading: isEntriesLoading } = useGetTimeSheetEntries({
    pageNo: 1,
    pageSize: 500,
    filter: {
      UserId: userId,
      TaskDate: taskDateRangeClause(range.from, range.to),
      ...(customerId ? { CustomerIds: { $in: [customerId] } } : {}),
      ...(projectId ? { ProjectIds: { $in: [projectId] } } : {}),
    },
    order: DEFAULT_TIMESHEETS_ORDER,
  });
  const entries = (entriesData as { TimeSheets?: TimeSheetsPagingResult })?.TimeSheets?.items ?? [];

  const inScope = (task: TimeSheetTask) =>
    (!customerId || task.CustomerId === customerId) && (!projectId || task.ProjectId === projectId);
  const days: ScopedDay[] = entries
    .map((record) => {
      const day = record.TaskDate.slice(0, 10);
      return { day, record, tasks: record.Tasks.filter(inScope), target: dayTarget(day, record.Tasks) };
    })
    .filter((day) => day.tasks.length > 0);

  const sumOf = (field: 'Billable' | 'NonBillable') =>
    days.reduce(
      (sum, day) => sum + day.tasks.reduce((daySum, task) => daySum + (Number(task[field]) || 0), 0),
      0
    );
  const billable = sumOf('Billable');
  const nonBillable = sumOf('NonBillable');
  const underTimeDays = days.filter((day) => day.target === 'under').length;

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          {/* The employee's name and department come with the breakdown: placeholders until then
              rather than the generic title. */}
          {isRollupLoading && !resource ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-7 w-48" />
              <Skeleton className="h-4 w-32" />
            </div>
          ) : (
            <>
              <h1 className="break-words text-2xl font-semibold">
                {resource?.ResourceName || t('TIMESHEET_MANAGER_TIMESHEET_DETAIL')}
              </h1>
              {resource?.DepartmentName && (
                <p className="text-sm text-muted-foreground">{resource.DepartmentName}</p>
              )}
            </>
          )}
        </div>
        <DateRangePicker
          value={range}
          onChange={(next) => setFilters({ range: next })}
          aria-label={t('DATE_RANGE')}
        />
      </div>

      <StatStrip
        isLoading={isEntriesLoading}
        items={[
          { label: t('TIMESHEET_MANAGER_GRAND_TOTAL'), value: formatHours(billable + nonBillable) },
          { label: t('TIMESHEET_MANAGER_BILLABLE'), value: formatHours(billable) },
          { label: t('TIMESHEET_MANAGER_NON_BILLABLE'), value: formatHours(nonBillable) },
          { label: t('TIMESHEET_MANAGER_DAYS_LOGGED'), value: days.length },
          {
            label: t('TIMESHEET_MANAGER_UNDER_TIME_DAYS'),
            value: underTimeDays,
            tone: underTimeDays ? 'destructive' : 'default',
          },
        ]}
      />

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold">{t('TIMESHEET_MANAGER_CUSTOMER_PROJECT_BREAKDOWN')}</h2>
        {isRollupLoading ? (
          <DetailRowsSkeleton rows={4} />
        ) : (
          <TimesheetBreakdownTable
            resource={
              resource ?? {
                UserId: userId,
                ResourceName: '',
                Customers: [],
                GrandTotal: 0,
              }
            }
          />
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h2 className="text-base font-semibold">{t('TIMESHEET_MANAGER_DAILY_ENTRIES')}</h2>
        {isEntriesLoading ? <DetailRowsSkeleton rows={5} /> : <TimesheetWeeks days={days} />}
      </section>
    </div>
  );
};
